import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, Chapter } from '../services/api';
import { API_BASE_URL } from '../services/api';
import { Course, Section, ChatMessage, Progress } from '../types';
import OutlinePanel from '../components/OutlinePanel';
import ContentViewer from '../components/ContentViewer';
import ChatPanel from '../components/ChatPanel';
import { Loader2, ChevronLeft, ChevronRight, ClipboardList } from 'lucide-react';

type LessonLanguage = 'en' | 'zh';

const prefixElementIds = (root: HTMLElement, prefix: string) => {
  const idMap = new Map<string, string>();
  root.querySelectorAll<HTMLElement>('[id]').forEach((element) => {
    const oldId = element.id;
    const newId = oldId.startsWith(`${prefix}-`) ? oldId : `${prefix}-${oldId}`;
    element.id = newId;
    idMap.set(oldId, newId);
  });

  root.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((anchor) => {
    const oldId = anchor.getAttribute('href')?.slice(1);
    if (oldId && idMap.has(oldId)) {
      anchor.setAttribute('href', `#${idMap.get(oldId)}`);
    }
  });
};

const getLanguageContent = (content: string, language: LessonLanguage, scopePrefix?: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const panels = Array.from(doc.querySelectorAll<HTMLElement>('[data-language]'));
  if (panels.length === 0) return content;

  panels.forEach((panel) => {
    if (panel.dataset.language !== language) {
      panel.remove();
      return;
    }

    const fragment = doc.createDocumentFragment();
    while (panel.firstChild) {
      fragment.appendChild(panel.firstChild);
    }
    panel.replaceWith(fragment);
  });

  if (scopePrefix) {
    prefixElementIds(doc.body, scopePrefix);
  } else {
    doc.querySelectorAll<HTMLElement>('.chapter-wrapper[data-chapter]').forEach((chapter) => {
      const chapterNumber = chapter.dataset.chapter;
      if (chapterNumber) {
        prefixElementIds(chapter, `chapter-${chapterNumber}`);
      }
    });
  }

  return doc.body.innerHTML;
};

const cleanLessonContent = (html: string, courseId: number): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll<HTMLElement>('.chapter-wrapper[data-chapter]').forEach((chapter) => {
    const chapterNumber = chapter.dataset.chapter;
    if (!chapterNumber) return;
    chapter.querySelectorAll<HTMLAnchorElement>('a[href*="questions"]').forEach((anchor) => {
      anchor.href = `/practice/${courseId}/${chapterNumber}`;
      anchor.removeAttribute('target');
      anchor.classList.add('ds-question-notebook-link');
    });
  });
  doc.querySelectorAll<HTMLAnchorElement>('a[href*="questions"]').forEach((anchor) => {
    const weekMatch = anchor.getAttribute('href')?.match(/week(\d+)/);
    if (weekMatch) {
      anchor.href = `/practice/${courseId}/${Number(weekMatch[1])}`;
      anchor.removeAttribute('target');
      anchor.classList.add('ds-question-notebook-link');
    }
  });
  doc.querySelectorAll<HTMLAnchorElement>('a').forEach((anchor) => {
    if (/question notebook|本周.*习题|打开本周/i.test(anchor.textContent || '')) {
      anchor.classList.add('ds-question-notebook-link');
    }
  });
  doc.querySelectorAll('.ds-practice-section, .crumbs, .eyebrow').forEach((element) => element.remove());
  return doc.body.innerHTML;
};

const getChapterTitle = (chapter: Chapter, language: LessonLanguage): string => {
  if (!chapter.content) return chapter.title;
  const html = getLanguageContent(chapter.content, language, `chapter-${chapter.chapter_number}`);
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector('h1')?.textContent?.trim() || chapter.title;
};

// Parse course outline from HTML content or outline string
const parseOutline = (course: Course, chapters: Chapter[] = [], language: LessonLanguage = 'en'): Section[] => {
  if (chapters.length > 0) {
    return chapters
      .slice()
      .sort((a, b) => (a.order || a.chapter_number) - (b.order || b.chapter_number))
      .map((chapter, chapterIndex) => {
        const chapterId = `chapter-${chapter.chapter_number}`;

        return {
          id: chapterId,
          title: getChapterTitle(chapter, language),
          order: chapterIndex,
          subsections: [],
        };
      });
  }

  // Try to extract from HTML content
  if (course.content) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(course.content, 'text/html');
    const headings = doc.querySelectorAll('h1, h2');
    
    const sections: Section[] = [];
    let currentSection: Section | null = null;
    
    headings.forEach((heading, idx) => {
      const level = heading.tagName === 'H1' ? 1 : 2;
      const title = heading.textContent || '';
      const container = heading.closest('section, article') as HTMLElement | null;
      const id = container?.id || (heading as HTMLElement).id || `section-${idx}`;
      
      if (level === 1) {
        currentSection = {
          id,
          title,
          order: sections.length,
          subsections: [],
        };
        sections.push(currentSection);
      } else if (currentSection) {
        currentSection.subsections.push({
          id,
          title,
          order: currentSection.subsections.length,
        });
      }
    });
    
    if (sections.length > 0) {
      return sections;
    }
  }
  
  // Fallback: generate from outline text
  if (course.outline) {
    const lines = course.outline.split('\n').filter(l => l.trim());
    const sections: Section[] = [];
    let currentSection: Section | null = null;
    
    lines.forEach((line, idx) => {
      const match = line.match(/^(Section\s+\d+|\d+)\.?\s*(.+)/i);
      if (match) {
        currentSection = {
          id: `section-${idx}`,
          title: match[2],
          order: sections.length,
          subsections: [],
        };
        sections.push(currentSection);
      } else if (line.match(/^\d+\.\d+/) && currentSection) {
        const subMatch = line.match(/^(\d+\.\d+)\s+(.+)/);
        if (subMatch) {
          currentSection.subsections.push({
            id: `section-${idx}`,
            title: subMatch[2],
            order: currentSection.subsections.length,
          });
        }
      }
    });
    
    if (sections.length > 0) {
      return sections;
    }
  }
  
  // Default fallback
  return [
    {
      id: 'section-1',
      title: course.title,
      order: 0,
      subsections: [
        { id: 'section-1-1', title: 'Introduction', order: 0 },
      ],
    },
  ];
};

export default function Learn() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [currentSection, setCurrentSection] = useState<string>('');
  const [progress, setProgress] = useState<Progress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [language, setLanguage] = useState<LessonLanguage>(() => {
    return localStorage.getItem('ds101_lesson_language') === 'zh' ? 'zh' : 'en';
  });
  const defaultWelcomeMessage: ChatMessage = { 
    role: 'assistant', 
    content: 'Hi! I\'m your Data Science tutor. Feel free to ask me anything about the course material!' 
  };
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([defaultWelcomeMessage]);
  const [showChat, setShowChat] = useState(true);

  // Load chat history from backend when course loads
  useEffect(() => {
    if (courseId) {
      const courseIdNum = parseInt(courseId);
      loadCourse(courseIdNum);
      loadChatHistory(courseIdNum);
    }
  }, [courseId]);

  // Save chat history when messages change
  useEffect(() => {
    if (courseId && chatMessages.length > 1) {
      const courseIdNum = parseInt(courseId);
      // Debounce save to avoid too many requests
      const timeoutId = setTimeout(() => {
        api.saveChatHistory(courseIdNum, chatMessages).catch(console.error);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [chatMessages, courseId]);

  const loadCourse = async (id: number) => {
    try {
      const [courseData, progressData, chapterData] = await Promise.all([
        api.getCourse(id),
        api.getProgress(id),
        api.getPublishedChapters(id),
      ]);
      
      setCourse(courseData);
      setProgress(progressData);
      setChapters(chapterData);
    } catch (error) {
      console.error('Failed to load course:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!course || chapters.length === 0) return;

    localStorage.setItem('ds101_lesson_language', language);
    const parsedSections = parseOutline(course, chapters, language);
    setSections(parsedSections);

    if (parsedSections.length > 0) {
      setCurrentSection((current) => {
        if (parsedSections.some(section => section.id === current)) return current;
        return parsedSections[0].id;
      });
    }
  }, [course, chapters, language]);

  const loadChatHistory = async (id: number) => {
    try {
      const history = await api.getChatHistory(id);
      if (history && history.length > 0) {
        setChatMessages(history);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const clearChatHistory = async () => {
    if (courseId) {
      try {
        await api.clearChatHistory(parseInt(courseId));
        setChatMessages([defaultWelcomeMessage]);
      } catch (error) {
        console.error('Failed to clear chat history:', error);
      }
    }
  };

  const handleSelectSection = useCallback((sectionId: string) => {
    setCurrentSection(sectionId);
    
    // Scroll to the corresponding section in content
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    
    // Mark as completed when viewed
    if (courseId) {
      api.updateProgress(parseInt(courseId), sectionId, true).then(updated => {
        setProgress(prev => {
          const filtered = prev.filter(p => p.section_id !== sectionId);
          return [...filtered, updated];
        });
      });
    }
  }, [courseId]);

  const handleAskQuestion = (selectedText: string, question: string) => {
    // Add to chat messages and open chat panel
    const userMessage: ChatMessage = {
      role: 'user',
      content: `user selected "${selectedText.substring(0, 100)}${selectedText.length > 100 ? '...' : ''}" in the content and ask: ${question}`,
    };
    
    setChatMessages(prev => [...prev, userMessage]);
    setShowChat(true);
    
    // Use the chat API with context
    fetch(`${API_BASE_URL}/student/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify({
        messages: [...chatMessages, userMessage],
        context: selectedText,
        stream: true,
      }),
    }).then(async (response) => {
      if (!response.ok) throw new Error('Failed to get response');
      
      const reader = response.body?.getReader();
      if (!reader) return;
      
      let content = '';
      setChatMessages(prev => [...prev, { role: 'assistant', content: '' }]);
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = new TextDecoder().decode(value);
        content += chunk;
        
        setChatMessages(prev => [
          ...prev.slice(0, -1),
          { role: 'assistant', content },
        ]);
      }
    }).catch((error) => {
      console.error('Chat error:', error);
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' },
      ]);
    });
  };

  const getNavigableSectionIds = () =>
    sections.flatMap(section =>
      section.subsections.length > 0
        ? section.subsections.map(subsection => subsection.id)
        : [section.id]
    );

  const findNextSection = () => {
    const ids = getNavigableSectionIds();
    const index = ids.indexOf(currentSection);
    return index >= 0 && index < ids.length - 1 ? ids[index + 1] : null;
  };

  const findPrevSection = () => {
    const ids = getNavigableSectionIds();
    const index = ids.indexOf(currentSection);
    return index > 0 ? ids[index - 1] : null;
  };

  const handleNext = () => {
    const next = findNextSection();
    if (next) handleSelectSection(next);
  };

  const handlePrev = () => {
    const prev = findPrevSection();
    if (prev) handleSelectSection(prev);
  };

  const visibleCourseContent = useMemo(() => {
    if (!course?.content) return '';
    return cleanLessonContent(getLanguageContent(course.content, language), course.id);
  }, [course?.content, course?.id, language]);

  if (isLoading) {
    return (
      <div className="h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-apple-blue animate-spin" />
          <p className="text-apple-text-secondary">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-apple-text mb-2">Course not found</h2>
          <button
            onClick={() => navigate('/courses')}
            className="btn-primary"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  // Extract current section content
  const currentSectionIndex = sections.findIndex(s =>
    s.id === currentSection || s.subsections.some(sub => sub.id === currentSection)
  );
  const currentSubsectionIndex = currentSectionIndex >= 0
    ? sections[currentSectionIndex].subsections.findIndex(sub => sub.id === currentSection)
    : -1;
  const currentChapterNumber = currentSection.match(/^chapter-(\d+)$/)?.[1]
    || (currentSectionIndex >= 0 ? String(currentSectionIndex + 1) : '1');

  return (
    <div className="h-[calc(100vh-64px)] flex">
      {/* Left Sidebar - Outline */}
      <div className="w-72 flex-shrink-0">
        <OutlinePanel
          sections={sections}
          currentSection={currentSection}
          progress={progress}
          onSelectSection={handleSelectSection}
        />
      </div>

      {/* Center - Content */}
      <div className="flex-1 flex flex-col min-w-0 bg-white">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-apple-border">
          <div>
            <h1 className="text-xl font-semibold text-apple-text">{course.title}</h1>
            {currentSectionIndex >= 0 && (
              <p className="text-sm text-apple-text-secondary">
                {currentSubsectionIndex >= 0
                  ? `${sections[currentSectionIndex].title} / ${sections[currentSectionIndex].subsections[currentSubsectionIndex].title}`
                  : sections[currentSectionIndex].title}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/practice/${course.id}/${currentChapterNumber}`)}
              className="btn-primary flex items-center gap-2 text-sm shadow-apple-sm"
            >
              <ClipboardList className="w-4 h-4" />
              {language === 'en' ? 'Practice' : '习题'}
            </button>
            <div className="inline-flex rounded-full border border-apple-border bg-white p-1">
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  language === 'en'
                    ? 'bg-apple-blue text-white'
                    : 'text-apple-text-secondary hover:text-apple-text'
                }`}
              >
                English
              </button>
              <button
                onClick={() => setLanguage('zh')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  language === 'zh'
                    ? 'bg-apple-blue text-white'
                    : 'text-apple-text-secondary hover:text-apple-text'
                }`}
              >
                中文
              </button>
            </div>
            <button
              onClick={() => setShowChat(!showChat)}
              className="btn-secondary text-sm"
            >
              {showChat ? 'Hide Chat' : 'Show Chat'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-8 py-8">
            {visibleCourseContent ? (
              <ContentViewer 
                content={visibleCourseContent}
                onAskQuestion={handleAskQuestion}
              />
            ) : (
              <div className="text-center py-16">
                <p className="text-apple-text-secondary">No content available yet.</p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-12 pt-8 border-t border-apple-border">
              <button
                onClick={handlePrev}
                disabled={!findPrevSection()}
                className="btn-outline flex items-center gap-2 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
                {language === 'en' ? 'Previous' : '上一节'}
              </button>
              
              <button
                onClick={handleNext}
                disabled={!findNextSection()}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
              >
                {language === 'en' ? 'Next' : '下一节'}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar - Chat */}
      {showChat && (
        <div className="w-96 flex-shrink-0 border-l border-apple-border">
          <ChatPanel 
            onClose={() => setShowChat(false)}
            externalMessages={chatMessages}
            onMessagesChange={setChatMessages}
            onClearHistory={clearChatHistory}
          />
        </div>
      )}
    </div>
  );
}
